import React, { FC } from 'react';
import {
  ComparePaymentPlanTypes,
  PaymentPlans,
  PharmacyBillingPageStatuses,
  pharmacyBillingSelector,
} from '@pharmaplan/common';

import { Box, SxProps } from '@mui/system';
import { Typography } from '@mui/material';

import useExistingPaymentPlanDates from '../../../../../hooks/Payment/useExistingPaymentPlanDates';
import { useAppDispatch } from '../../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import { setDialog } from '../../../../../reducers/dialogReducer';
import ComparePaymentPlan from '../../../../ComparePaymentPlan';
import { Constants } from '../../../../../helpers/Constants';
import CustomButton from '../../../../common/CustomButton';
import strings from '../../../../../localization';

import SubscriptionDialogs from './SubscriptionDialogs/SubscriptionDialogs';
import SavedPlanDetails from './SavedPlanDetails';
import styles from './styles';

interface IExistingPaymentPlan {
  setPageStatus: React.Dispatch<
    React.SetStateAction<PharmacyBillingPageStatuses>
  >;
}

const ExistingPaymentPlan: FC<IExistingPaymentPlan> = ({ setPageStatus }) => {
  const dispatch = useAppDispatch();
  const planDetails = useAppSelector(pharmacyBillingSelector);
  const { currentPlan, planFees, renewalDate, activeUntil } = planDetails;
  const canResumeSubscription = !renewalDate && activeUntil;

  const showComparePlan = () => {
    dispatch(
      setDialog({
        Component: (
          <ComparePaymentPlan screenType={ComparePaymentPlanTypes.view} />
        ),
        showCloseButton: false,
        maxWidth: 'md',
        heading: { title: '' },
      }),
    );
  };

  const {
    validUntil,
    renewal,
    label: validTillLabel,
  } = useExistingPaymentPlanDates();

  const { showCancelDialog, showOnPlanToUpgrade, reactivateDialog } = SubscriptionDialogs({
    setPageStatus,
  });

  const planConfig = {
    [PaymentPlans.basic]: {
      label: strings.standard,
      per: strings.contract,
      caption: strings.contractBasedPlanning,
      bottomRightButtonLabel: strings.upgradePlan,
      bottomRightButtonAction: showOnPlanToUpgrade,
      buttonContainer: styles.basicContainer,
    },
    [PaymentPlans.unlimited]: {
      label: strings.unlimited,
      per: strings.month,
      caption: strings.formatString(validTillLabel ?? '', validUntil, renewal),
      bottomRightButtonLabel: canResumeSubscription
        ? strings.reactivateSubscription
        : strings.cancelSubscription,
      bottomRightButtonAction: canResumeSubscription
        ? reactivateDialog
        : showCancelDialog,
      buttonContainer: styles.unlimitedContainer,
    },
  };

  const {
    per,
    caption,
    buttonContainer,
    bottomRightButtonAction,
    bottomRightButtonLabel,
    label,
  } = planConfig[
    (currentPlan?.toLowerCase() as PaymentPlans) ?? PaymentPlans.basic
  ];

  return (
    <>
      <Box sx={styles.container}>
        <Box sx={[styles.upperCard, styles.cancelStyle] as SxProps}>
          <Box>
            <Typography sx={[styles.title, styles.label]}>
              {strings.formatString(strings.planFormat, label ?? '')}
            </Typography>
            <Typography sx={[styles.caption, styles.label]}>
              {caption}
            </Typography>
          </Box>
          <Box>
            <Typography sx={styles.planFees}>
              $
              {planFees}
              <Typography component="span" sx={[styles.per, styles.label]}>
                {` /${per}`}
              </Typography>
            </Typography>
          </Box>
        </Box>
        <Box sx={[styles.subContainer, buttonContainer]}>
          <CustomButton
            onClick={bottomRightButtonAction}
            label={bottomRightButtonLabel}
            variant={Constants.variant.text}
            customButtonStyle={styles.buttonText}
          />
        </Box>
      </Box>
      <Box sx={styles.compareContainer}>
        <CustomButton onClick={showComparePlan} label={strings.comparePlan} />
      </Box>
      <SavedPlanDetails />
    </>
  );
};

export default ExistingPaymentPlan;
